import React from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  LocalDining as LocalDiningIcon,
  QrCode2 as QrCode2Icon,
  ReceiptLong as ReceiptLongIcon,
  RoomService as RoomServiceIcon,
  TakeoutDining as TakeoutDiningIcon,
} from '@mui/icons-material';
import {
  formatRestaurantCurrencyAmount,
  formatRestaurantOrderDateTime,
  formatRestaurantOrderNumber,
  getRestaurantOrderTypeLabel,
  INCOMING_ORDER_PREP_OPTIONS,
} from '../../utils/restaurantQrIncomingOrders';

const ACCENT = '#4ce1b6';

function getOrderLocationLabel(order) {
  if (order?.room_number) return `Room ${order.room_number}`;
  if (order?.table_name) return order.table_name;
  return 'Guest order';
}

function getOrderTypeIcon(orderType) {
  if (orderType === 'room_service') return RoomServiceIcon;
  if (orderType === 'takeaway') return TakeoutDiningIcon;
  return LocalDiningIcon;
}

export default function RestaurantQrOrderNotificationDialog({
  open,
  order,
  queueCount = 0,
  prepMinutes = 30,
  processing = false,
  actionError = '',
  onPrepMinutesChange,
  onAccept,
  onDecline,
  onClose,
  onOpenRestaurant,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!order) return null;

  const orderType = String(order?.order_type || '').toLowerCase();
  const TypeIcon = getOrderTypeIcon(orderType);
  const orderItems = Array.isArray(order?.items) ? order.items : [];

  return (
    <Dialog
      open={open}
      onClose={processing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          overflow: 'hidden',
          borderRadius: 3,
          border: `1px solid ${alpha(ACCENT, isDark ? 0.35 : 0.28)}`,
          background: isDark
            ? 'linear-gradient(165deg, rgba(18,24,36,0.98) 0%, rgba(10,16,28,0.98) 55%, rgba(8,14,24,0.98) 100%)'
            : 'linear-gradient(165deg, #ffffff 0%, #f3fffb 48%, #f7fbff 100%)',
          boxShadow: isDark
            ? '0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(76,225,182,0.12)'
            : '0 24px 60px rgba(15,23,42,0.18), 0 0 0 1px rgba(76,225,182,0.16)',
        },
      }}
    >
      <Box
        sx={{
          px: 2.2,
          py: 1.6,
          background: isDark
            ? 'linear-gradient(120deg, rgba(76,225,182,0.18) 0%, rgba(59,130,246,0.12) 100%)'
            : 'linear-gradient(120deg, rgba(76,225,182,0.18) 0%, rgba(59,130,246,0.08) 100%)',
          borderBottom: `1px solid ${alpha(ACCENT, 0.22)}`,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: '#0f172a',
                background: 'linear-gradient(135deg, #4ce1b6 0%, #2dd4bf 100%)',
                boxShadow: `0 10px 24px ${alpha(ACCENT, 0.35)}`,
              }}
            >
              <QrCode2Icon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.2 }}>
                New QR Order
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#cbd5e1' : '#475569', fontWeight: 600 }}>
                Guest placed an order from QR menu
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {queueCount > 1 ? (
              <Chip
                size="small"
                label={`${queueCount} pending`}
                sx={{
                  fontWeight: 800,
                  bgcolor: alpha('#f59e0b', isDark ? 0.22 : 0.16),
                  color: isDark ? '#fde68a' : '#92400e',
                }}
              />
            ) : null}
            <IconButton size="small" onClick={onClose} disabled={processing}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 2.2, py: 1.8 }}>
        <Box
          sx={{
            p: 1.35,
            mb: 1.4,
            borderRadius: 2,
            border: `1px solid ${alpha(ACCENT, isDark ? 0.24 : 0.18)}`,
            background: isDark ? alpha(ACCENT, 0.08) : alpha(ACCENT, 0.06),
          }}
        >
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
            <Chip
              icon={<ReceiptLongIcon sx={{ fontSize: '16px !important' }} />}
              label={`#${formatRestaurantOrderNumber(order?.order_number)}`}
              size="small"
              sx={{ fontWeight: 800 }}
            />
            <Chip
              icon={<TypeIcon sx={{ fontSize: '16px !important' }} />}
              label={getRestaurantOrderTypeLabel(orderType)}
              size="small"
              sx={{ fontWeight: 700 }}
            />
            <Chip label={getOrderLocationLabel(order)} size="small" sx={{ fontWeight: 700 }} />
          </Stack>

          <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.35 }}>
            {order?.customer_name || 'Guest'}
            {order?.customer_phone ? ` · ${order.customer_phone}` : ''}
          </Typography>
          <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
            {formatRestaurantOrderDateTime(order?.created_at)}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mt: 1,
              fontWeight: 900,
              color: isDark ? '#f8fafc' : '#0f172a',
            }}
          >
            {formatRestaurantCurrencyAmount(order?.total_amount)}
          </Typography>
          {String(order?.notes || '').trim() ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: isDark ? '#cbd5e1' : '#475569' }}>
              Note: {String(order.notes).trim()}
            </Typography>
          ) : null}
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
          Order Items
        </Typography>
        <Stack spacing={0.65} sx={{ mb: 1.5, maxHeight: 180, overflowY: 'auto' }}>
          {orderItems.slice(0, 8).map((item, index) => (
            <Box
              key={`qr-popup-item-${order.id}-${index}`}
              sx={{
                px: 1,
                py: 0.75,
                borderRadius: 1.5,
                border: `1px solid ${alpha(ACCENT, isDark ? 0.16 : 0.12)}`,
                background: isDark ? alpha('#fff', 0.03) : '#fff',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {item?.menu_item_name || item?.item_name || `Item #${index + 1}`}
                </Typography>
                <Chip
                  size="small"
                  label={`x${Math.max(1, Number(item?.quantity || 1))}`}
                  sx={{ fontWeight: 800, minWidth: 42 }}
                />
              </Stack>
            </Box>
          ))}
        </Stack>

        <FormControl fullWidth size="small" sx={{ mb: 1.2 }}>
          <InputLabel id="global-qr-prep-time-label">Preparation time</InputLabel>
          <Select
            labelId="global-qr-prep-time-label"
            label="Preparation time"
            value={prepMinutes}
            onChange={(event) => onPrepMinutesChange(Number(event.target.value))}
            disabled={processing}
          >
            {INCOMING_ORDER_PREP_OPTIONS.map((minutes) => (
              <MenuItem key={minutes} value={minutes}>
                {minutes} min
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {actionError ? (
          <Alert severity="error" sx={{ mb: 1.2, py: 0.2 }}>
            {actionError}
          </Alert>
        ) : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button
            fullWidth
            variant="outlined"
            color="error"
            disabled={processing}
            onClick={onDecline}
            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2 }}
          >
            {processing ? 'Updating...' : 'Decline'}
          </Button>
          <Button
            fullWidth
            variant="contained"
            disabled={processing}
            onClick={onAccept}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
              color: '#0f172a',
              background: 'linear-gradient(90deg, #4ce1b6 0%, #2dd4bf 100%)',
              '&:hover': {
                background: 'linear-gradient(90deg, #3dd1a8 0%, #22c997 100%)',
              },
            }}
          >
            {processing ? 'Updating...' : 'Accept Order'}
          </Button>
        </Stack>

        {typeof onOpenRestaurant === 'function' ? (
          <Button
            fullWidth
            variant="text"
            onClick={onOpenRestaurant}
            disabled={processing}
            sx={{ mt: 0.8, textTransform: 'none', fontWeight: 700, color: isDark ? ACCENT : '#0f766e' }}
          >
            Open Restaurant POS
          </Button>
        ) : null}
      </Box>
    </Dialog>
  );
}
